import { useEffect, useMemo, useState } from 'react'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Table from 'react-bootstrap/Table'

import {
  formatCurrency,
  formatNumber,
  getDefaultSeason,
} from '../../utilities/helpers'
import {
  getYoyDifferenceReport,
  saveYoyDifferenceConfig,
  getSeasons,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { LoadingContainer } from '../LoadingContainer'
import { EmptyContainer } from '../EmptyContainer'
import { SeasonSelector } from '../SeasonSelector'
import { ReportActions } from '../ReportActions'

import { exportYoyDifferenceExcel } from './yoyDifferenceExcelExport'
import YOYDifferenceEmailSchedule from './YOYDifferenceEmailSchedule'

import './yoyDifferenceReportWrapper.scss'

const CATEGORY_LABEL = 'Ticket Revenue'

// Fallback planning inputs (budget target + premium add-on), used only until an
// organization saves its own values via the editor below.
const DEFAULT_TOTAL_BUDGET = 1836000
const DEFAULT_FULL_SEASON_NEW_PREMIUM = 286953

const EMPTY_TOTALS = {
  fullSeasonRenewal: 0,
  fullSeasonNew: 0,
  flexPacksRenewal: 0,
  flexPacksNew: 0,
  groups: 0,
  singleGame: 0,
  seasonSeatBase: 0,
  seasonSeatsRenewal: 0,
  seasonSeatsRenewalComps: 0,
  seasonSeatsNew: 0,
}

const safePercent = (value, total) => {
  const normalizedTotal = Number(total || 0)
  if (!normalizedTotal) return null
  return Number(value || 0) / normalizedTotal
}

const formatPercent = (value, digits = 2) => {
  if (value == null) return 'N/A'
  return `${(value * 100).toFixed(digits)}%`
}

const formatMoney = (value) => {
  if (value == null) return 'NA'
  return formatCurrency(Number(value))
}

const toNumber = (value) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export default function YOYDifferenceReportWrapper({ organization }) {
  const [seasons, setSeasons] = useState([])
  const [selectedSeason, setSelectedSeason] = useState('')
  const [report, setReport] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const { refreshToken, refresh, completeRefresh } = useReportRefresh(isLoading)
  const [loadError, setLoadError] = useState('')

  const [config, setConfig] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [form, setForm] = useState({
    totalBudget: '',
    fullSeasonNewPremium: '',
    repGoals: {},
  })

  useEffect(() => {
    let cancelled = false

    const loadSeasons = async () => {
      if (!organization?.uuid) {
        setLoadError('Missing organization context for report.')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      setLoadError('')

      try {
        const seasonsRes = await getSeasons(organization.uuid)
        if (cancelled) return

        const loadedSeasons = seasonsRes?.data || []
        setSeasons(loadedSeasons)

        if (!loadedSeasons.length) {
          setLoadError('No seasons found for this organization.')
          setIsLoading(false)
          return
        }

        const defaultSeason = getDefaultSeason(loadedSeasons)
        setSelectedSeason(
          String(defaultSeason?.id || loadedSeasons[0]?.id || '')
        )
      } catch (error) {
        if (cancelled) return
        console.error('Error loading seasons for YOY Difference report:', error)
        setLoadError('Unable to load seasons for YOY Difference report.')
        setIsLoading(false)
      }
    }

    loadSeasons()

    return () => {
      cancelled = true
    }
  }, [organization?.uuid])

  useEffect(() => {
    let cancelled = false

    const loadReport = async () => {
      if (!organization?.uuid || !selectedSeason) return

      setIsLoading(true)
      setLoadError('')

      try {
        const reportRes = await getYoyDifferenceReport(
          organization.uuid,
          selectedSeason
        )

        if (cancelled) return
        setReport(reportRes?.data || null)
        setConfig(reportRes?.data?.config || null)
      } catch (error) {
        if (cancelled) return
        console.error('Error loading YOY Difference report data:', error)
        setLoadError('Unable to load YOY Difference report data.')
      } finally {
        if (!cancelled) {
          setIsLoading(false)
          completeRefresh()
        }
      }
    }

    loadReport()

    return () => {
      cancelled = true
    }
  }, [organization?.uuid, selectedSeason, refreshToken])

  const totalBudget =
    toNumberOrNull(config?.totalBudget) ?? DEFAULT_TOTAL_BUDGET
  const fullSeasonNewPremiumBreakdown =
    report?.fullSeasonNewPremiumBreakdown || null
  const computedFullSeasonNewPremium = toNumber(
    fullSeasonNewPremiumBreakdown?.total
  )
  const fullSeasonNewPremium =
    computedFullSeasonNewPremium > 0
      ? computedFullSeasonNewPremium
      : (toNumberOrNull(config?.fullSeasonNewPremium) ??
        DEFAULT_FULL_SEASON_NEW_PREMIUM)

  const repGoalMap = useMemo(() => {
    const map = new Map()
    ;(config?.repGoals || []).forEach((goal) => {
      const rep = String(goal?.rep || '').trim()
      if (rep) map.set(rep, goal)
    })
    return map
  }, [config])

  const previousTotals = report?.previous || EMPTY_TOTALS
  const currentTotals = report?.current || EMPTY_TOTALS
  const currentSeasonLabel = report?.seasons?.current?.name || 'Current Season'
  const previousSeasonLabel =
    report?.seasons?.previous?.name || 'Previous Season'

  const fullSeasonNewCategoryNote = useMemo(() => {
    if (
      !fullSeasonNewPremiumBreakdown ||
      (toNumber(fullSeasonNewPremiumBreakdown.club) === 0 &&
        toNumber(fullSeasonNewPremiumBreakdown.suite) === 0)
    ) {
      return null
    }

    return `club ${formatMoney(fullSeasonNewPremiumBreakdown.club)} + suite ${formatMoney(fullSeasonNewPremiumBreakdown.suite)} ${currentSeasonLabel} premium totals`
  }, [currentSeasonLabel, fullSeasonNewPremiumBreakdown])

  // Overall 25/26 base used for proportional budget allocation (renewals are NA).
  const overallPreviousBase = useMemo(
    () =>
      toNumber(previousTotals.fullSeasonNew) +
      toNumber(previousTotals.flexPacksNew) +
      toNumber(previousTotals.groups) +
      toNumber(previousTotals.singleGame),
    [previousTotals]
  )

  const allocateBudget = useMemo(() => {
    return (previousTotal, premium = 0) => {
      if (!overallPreviousBase) return null
      return (
        (toNumber(previousTotal) / overallPreviousBase) * totalBudget + premium
      )
    }
  }, [overallPreviousBase, totalBudget])

  const productRows = useMemo(
    () => [
      {
        product: 'Full Season Renewal',
        note: null,
        previousTotal: previousTotals.fullSeasonRenewal,
        budget: allocateBudget(previousTotals.fullSeasonRenewal),
        ytd: currentTotals.fullSeasonRenewal,
      },
      {
        product: 'Full Season New',
        note: fullSeasonNewCategoryNote,
        previousTotal: previousTotals.fullSeasonNew,
        budget: allocateBudget(
          previousTotals.fullSeasonNew,
          fullSeasonNewPremium
        ),
        ytd: currentTotals.fullSeasonNew,
      },
      {
        product: 'Flex Packs Renewal',
        note: null,
        previousTotal: null,
        budget: null,
        ytd: currentTotals.flexPacksRenewal,
      },
      {
        product: 'Flex Packs New',
        note: null,
        previousTotal: previousTotals.flexPacksNew,
        budget: allocateBudget(previousTotals.flexPacksNew),
        ytd: currentTotals.flexPacksNew,
      },
      {
        product: 'Groups',
        note: null,
        previousTotal: previousTotals.groups,
        budget: allocateBudget(previousTotals.groups),
        ytd: currentTotals.groups,
      },
      {
        product: 'Single Game',
        note: null,
        previousTotal: previousTotals.singleGame,
        budget: allocateBudget(previousTotals.singleGame),
        ytd: currentTotals.singleGame,
      },
    ],
    [
      allocateBudget,
      currentTotals,
      fullSeasonNewPremium,
      fullSeasonNewCategoryNote,
      previousTotals,
    ]
  )

  const totalYtd = useMemo(
    () => productRows.reduce((sum, row) => sum + toNumber(row.ytd), 0),
    [productRows]
  )
  const overallYoyDiff = formatPercent(
    overallPreviousBase ? 1 - overallPreviousBase / totalBudget : null,
    2
  )
  const overallPctToBudget = formatPercent(
    safePercent(totalYtd, totalBudget),
    2
  )

  const seasonSummaryNotes = useMemo(() => {
    const previousSeasonSeatBase = toNumber(previousTotals.seasonSeatBase)
    const ytdRenewals = toNumber(currentTotals.seasonSeatsRenewal)
    const ytdRenewalComps = toNumber(
      currentTotals.seasonSeatsRenewalComps ??
        currentTotals.season_seats_renewal_comps
    )
    const ytdNew = toNumber(currentTotals.seasonSeatsNew)
    const ytdTotal = ytdRenewals + ytdNew
    const renewalRate = safePercent(ytdRenewals, previousSeasonSeatBase)
    const nextTarget = Math.round(previousSeasonSeatBase * 1.03)
    const ytdRenewalsLabel =
      ytdRenewalComps > 0
        ? `YTD Renewals = ${formatNumber(ytdRenewals)} (includes ${formatNumber(ytdRenewalComps)} comps)`
        : `YTD Renewals = ${formatNumber(ytdRenewals)}`

    return [
      `Season Ticket Summary (${formatNumber(previousSeasonSeatBase)} last years base)`,
      ytdRenewalsLabel,
      `YTD New Sales = ${formatNumber(ytdNew)}`,
      `YTD Total = ${formatNumber(ytdTotal)} seats`,
      `YTD Renewal Rate = ${formatPercent(renewalRate, 0)}`,
      `${currentSeasonLabel} Target = ${formatNumber(nextTarget)} (3% lift YOY)`,
    ]
  }, [currentSeasonLabel, currentTotals, previousTotals])

  const salesRepRows = useMemo(() => {
    const byRep = new Map()
    ;(report?.reps || []).forEach((row) => {
      const rep = String(row?.rep || 'Other').trim() || 'Other'
      byRep.set(rep, {
        rep,
        seasonActual: toNumber(row.seasonActual),
        flexActual: toNumber(row.flexActual),
        groupsActual: toNumber(row.groupsActual),
        singlesActual: toNumber(row.singlesActual),
      })
    })

    // Include reps that only exist in saved goals so their targets stay visible.
    repGoalMap.forEach((_goal, rep) => {
      if (!byRep.has(rep)) {
        byRep.set(rep, {
          rep,
          seasonActual: 0,
          flexActual: 0,
          groupsActual: 0,
          singlesActual: 0,
        })
      }
    })

    return Array.from(byRep.values()).sort(
      (a, b) =>
        b.seasonActual +
        b.groupsActual +
        b.singlesActual +
        b.flexActual -
        (a.seasonActual + a.groupsActual + a.singlesActual + a.flexActual)
    )
  }, [report, repGoalMap])

  const beginEditing = () => {
    const repGoals = {}
    salesRepRows.forEach((row) => {
      const saved = repGoalMap.get(row.rep) || {}
      repGoals[row.rep] = {
        seasonGoal: saved.seasonGoal == null ? '' : String(saved.seasonGoal),
        flexGoal: saved.flexGoal == null ? '' : String(saved.flexGoal),
        groupsGoal: saved.groupsGoal == null ? '' : String(saved.groupsGoal),
        singlesGoal: saved.singlesGoal == null ? '' : String(saved.singlesGoal),
      }
    })
    setForm({
      totalBudget: String(totalBudget),
      fullSeasonNewPremium: String(fullSeasonNewPremium),
      repGoals,
    })
    setSaveError('')
    setIsEditing(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setSaveError('')
  }

  const getActionButtons = () => {
    if (isEditing) {
      return (
        <>
          <Button
            variant="outline-primary"
            className="form-control-height"
            onClick={cancelEditing}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="form-control-height"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save Targets & Goals'}
          </Button>
        </>
      )
    }

    return (
      <Button
        variant="outline-primary"
        className="form-control-height"
        onClick={beginEditing}
      >
        Edit Targets
      </Button>
    )
  }

  const updateRepGoal = (rep, field, value) => {
    setForm((prev) => ({
      ...prev,
      repGoals: {
        ...prev.repGoals,
        [rep]: {
          ...prev.repGoals[rep],
          [field]: value,
        },
      },
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError('')
    try {
      const repGoals = Object.entries(form.repGoals)
        .map(([rep, goals]) => ({
          rep,
          seasonGoal: toNumberOrNull(goals.seasonGoal),
          flexGoal: toNumberOrNull(goals.flexGoal),
          groupsGoal: toNumberOrNull(goals.groupsGoal),
          singlesGoal: toNumberOrNull(goals.singlesGoal),
        }))
        .filter(
          (goal) =>
            goal.seasonGoal != null ||
            goal.flexGoal != null ||
            goal.groupsGoal != null ||
            goal.singlesGoal != null
        )

      const payload = {
        organizationUUID: organization.uuid,
        totalBudget: toNumberOrNull(form.totalBudget) ?? 0,
        fullSeasonNewPremium: toNumberOrNull(form.fullSeasonNewPremium) ?? 0,
        repGoals,
      }

      const response = await saveYoyDifferenceConfig(payload)
      setConfig(response?.data || payload)
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving YOY Difference config:', error)
      setSaveError('Unable to save targets. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  if (isLoading) {
    return (
      <section className="spacer-sm" id="yoy-difference-report-wrapper">
        <LoadingContainer />
      </section>
    )
  }

  if (loadError) {
    return (
      <section className="spacer-sm" id="yoy-difference-report-wrapper">
        <Card body>
          <EmptyContainer style="center lg report">
            <p>{loadError}</p>
          </EmptyContainer>
        </Card>
      </section>
    )
  }

  const getRepGoalValue = (rep, field) => {
    if (isEditing) {
      return toNumberOrNull(form.repGoals[rep]?.[field])
    }
    return toNumberOrNull(repGoalMap.get(rep)?.[field])
  }

  const handleExcelExport = async () => {
    if (!report) return

    try {
      await exportYoyDifferenceExcel({
        organizationName: organization?.name,
        previousSeasonLabel,
        currentSeasonLabel,
        categoryLabel: CATEGORY_LABEL,
        overallPreviousBase,
        totalBudget,
        productRows,
        seasonSummaryNotes,
        salesRepRows,
        getRepGoalValue,
      })
    } catch (error) {
      console.error('YOY Difference Excel export failed:', error)
    }
  }

  const renderGoalCell = (row, field) => {
    if (isEditing) {
      return (
        <td className="text-end">
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            size="sm"
            className="yoy-goal-input"
            value={form.repGoals[row.rep]?.[field] ?? ''}
            onChange={(event) =>
              updateRepGoal(row.rep, field, event.target.value)
            }
          />
        </td>
      )
    }
    return (
      <td className="text-end">
        {formatMoney(getRepGoalValue(row.rep, field))}
      </td>
    )
  }

  const renderTargetCell = (row, goalField, actualField) => {
    const pct = safePercent(
      row[actualField],
      getRepGoalValue(row.rep, goalField)
    )
    return <td className="text-end">{formatPercent(pct, 0)}</td>
  }

  return (
    <section className="spacer-sm" id="yoy-difference-report-wrapper">
      <header className="section-header-sm yoy-report-header d-flex justify-content-between align-items-end">
        <div className="section-heading">
          <h1>YOY Difference</h1>
        </div>
        {seasons.length > 0 ? (
          <ReportActions
            isLoading={isLoading}
            onRefresh={refresh}
            isRefreshing={isLoading}
            selected={selectedSeason}
            data={report}
            downloadReport={handleExcelExport}
            getButton={getActionButtons}
          >
            <SeasonSelector
              seasons={seasons}
              selected={selectedSeason}
              setSelected={setSelectedSeason}
              isLoading={isLoading}
            />
          </ReportActions>
        ) : (
          <div className="d-flex gap-2 align-items-end yoy-report-header__actions">
            {getActionButtons()}
          </div>
        )}
      </header>

      {saveError ? <p className="text-danger small mb-2">{saveError}</p> : null}

      <YOYDifferenceEmailSchedule
        organizationUUID={organization?.uuid}
        selectedSeason={selectedSeason}
      />

      {isEditing ? (
        <Card body className="mb-3 yoy-budget-editor">
          <Row className="g-3">
            <Col md={4}>
              <Form.Label className="small fw-semibold mb-1">
                {currentSeasonLabel} Budget Target
              </Form.Label>
              <Form.Control
                type="number"
                value={form.totalBudget}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    totalBudget: event.target.value,
                  }))
                }
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-semibold mb-1">
                Full Season New Premium
              </Form.Label>
              <Form.Control
                type="number"
                value={form.fullSeasonNewPremium}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    fullSeasonNewPremium: event.target.value,
                  }))
                }
              />
            </Col>
          </Row>
        </Card>
      ) : null}

      <Card body className="mb-3">
        <Table responsive className="align-middle mb-0 yoy-main-table">
          <thead>
            <tr>
              <th>Category</th>
              <th>Products</th>
              <th className="text-end">{previousSeasonLabel} Total</th>
              <th className="text-end">{currentSeasonLabel} Budget</th>
              <th className="text-end">YOY Difference</th>
              <th className="text-end">{currentSeasonLabel} YTD</th>
              <th className="text-end">% to Budget</th>
            </tr>
          </thead>
          <tbody>
            <tr className="summary-row">
              <td>{CATEGORY_LABEL}</td>
              <td></td>
              <td className="text-end">{formatMoney(overallPreviousBase)}</td>
              <td className="text-end">{formatMoney(totalBudget)}</td>
              <td className="text-end">{overallYoyDiff}</td>
              <td className="text-end">{formatMoney(totalYtd)}</td>
              <td className="text-end">{overallPctToBudget}</td>
            </tr>
            {productRows.map((row) => {
              const yoyDiff =
                row.previousTotal != null && row.budget
                  ? formatPercent(1 - row.previousTotal / row.budget, 2)
                  : ''
              return (
                <tr key={row.product}>
                  <td className="yoy-category-note">{row.note || ''}</td>
                  <td>{row.product}</td>
                  <td className="text-end">{formatMoney(row.previousTotal)}</td>
                  <td className="text-end">{formatMoney(row.budget)}</td>
                  <td className="text-end">{yoyDiff}</td>
                  <td className="text-end">{formatMoney(row.ytd)}</td>
                  <td className="text-end">
                    {formatPercent(safePercent(row.ytd, row.budget), 2)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </Card>

      <Card body className="mb-3 yoy-summary-card">
        <h2 className="h5 mb-3">{seasonSummaryNotes[0]}</h2>
        <ul className="season-summary-notes list-unstyled mb-0">
          {seasonSummaryNotes.slice(1).map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </Card>

      <Card body>
        {isEditing ? (
          <p className="small text-muted mb-3">
            Enter a goal amount for each sales rep below. % to Target updates as
            you type and is calculated as actual ÷ goal.
          </p>
        ) : null}
        <Table responsive className="align-middle mb-0 yoy-rep-table">
          <thead>
            <tr>
              <th rowSpan={2} className="align-bottom">
                Sales Rep
              </th>
              <th colSpan={3} className="text-center">
                Season Ticket
              </th>
              <th colSpan={3} className="text-center">
                Flex Packs
              </th>
              <th colSpan={3} className="text-center">
                Groups
              </th>
              <th colSpan={3} className="text-center">
                Singles
              </th>
            </tr>
            <tr>
              <th className="text-end">Goal</th>
              <th className="text-end">Actual</th>
              <th className="text-end">% to Target</th>
              <th className="text-end">Goal</th>
              <th className="text-end">Actual</th>
              <th className="text-end">% to Target</th>
              <th className="text-end">Goal</th>
              <th className="text-end">Actual</th>
              <th className="text-end">% to Target</th>
              <th className="text-end">Goal</th>
              <th className="text-end">Actual</th>
              <th className="text-end">% to Target</th>
            </tr>
          </thead>
          <tbody>
            {salesRepRows.length ? (
              salesRepRows.map((row) => (
                <tr key={row.rep}>
                  <td>{row.rep}</td>
                  {renderGoalCell(row, 'seasonGoal')}
                  <td className="text-end">{formatMoney(row.seasonActual)}</td>
                  {renderTargetCell(row, 'seasonGoal', 'seasonActual')}
                  {renderGoalCell(row, 'flexGoal')}
                  <td className="text-end">{formatMoney(row.flexActual)}</td>
                  {renderTargetCell(row, 'flexGoal', 'flexActual')}
                  {renderGoalCell(row, 'groupsGoal')}
                  <td className="text-end">{formatMoney(row.groupsActual)}</td>
                  {renderTargetCell(row, 'groupsGoal', 'groupsActual')}
                  {renderGoalCell(row, 'singlesGoal')}
                  <td className="text-end">{formatMoney(row.singlesActual)}</td>
                  {renderTargetCell(row, 'singlesGoal', 'singlesActual')}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={13} className="text-center text-muted py-4">
                  No sales rep activity for this period.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Card>
    </section>
  )
}
